
import { Component, Vue, Prop, Watch, Ref } from "vue-property-decorator"
import { Getter } from "vuex-class"
import vSelect from "vue-select"

import { CaseUser } from "@/types"
import { TimeEntry as TimeEntryType, TimeEntryInputFormat } from "@/types/timeEntry"
import { LocationLabels } from "@/types/location"
import { TenantFeatures } from "@/types/tenantFeatures"

import TimeDurationPicker from "./TimeDurationPicker.vue"
import TimeEntries from "@/components/TimeEntries.vue"

@Component({
	components: {
		"v-select": vSelect,
		"time-duration-picker": TimeDurationPicker,
		"time-entries": TimeEntries
	}
})
export default class UserRoleDetails extends Vue {
	/* --- Refs --- */

	@Ref()
	timeEntries!: TimeEntries

	/* --- Vuex --- */

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	/* --- Props --- */

	@Prop({ type: Object, required: true })
	value!: CaseUser

	@Prop({ type: Boolean, default: false })
	allowTimeTracking!: boolean

	@Prop({ type: String, default: TimeEntryInputFormat.Decimal })
	timeEntryInputFormat!: TimeEntryInputFormat

	@Prop({ type: String, default: TimeEntryInputFormat.Decimal })
	timeDisplayFormat!: TimeEntryInputFormat

	@Prop({ type: Boolean, default: false })
	displayLocation!: boolean

	@Prop({ type: Boolean, default: false })
	hideTimeSpentControls!: boolean

	@Prop({ type: Boolean, default: false })
	appendDropdownToBody!: boolean

	@Prop({ type: Boolean, default: false })
	readonly!: boolean

	/* --- Data --- */

	TimeEntryInputFormat = TimeEntryInputFormat
	LocationLabels = LocationLabels

	user: CaseUser = { ...this.value }

	/* --- Watchers --- */

	@Watch("value", { immediate: true, deep: true })
	onUserChange (newVal: CaseUser) {
		this.user = { ...newVal }
	}

	/* --- Computed Properties --- */

	private get userRoleTimeTrackingTimeEntriesFeatureEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.UserRoleTimeTrackingTimeEntries)
	}

	private get showTimeTracking (): boolean {
		return this.allowTimeTracking && !this.hideTimeSpentControls
	}

	/* --- Methods --- */

	/**
	 * Calculates total time spent by summing the `time` field of each entry.
	 * @param entries Array of time entry objects.
	 * @returns Total time spent as a number.
	 */
	private calculateTotalTimeSpent (entries: TimeEntryType[]): number {
		return entries.reduce((sum, entry) => sum + (entry.time || 0), 0)
	}

	/**
	 * Updates the local user's `timeSpent` value when time input changes.
	 * @param timeSpent The new time spent value entered by the user.
	 */
	private onTimeSpentInput (timeSpent: number | undefined) {
		this.user.timeSpent = timeSpent
	}

	/**
	 * Should be called when the time spent input loses focus.
	 * Applies any changes made to the total time spent to the time entries,
	 * or just emits the updated user if the `time entries` feature is not enabled.
	 */
	private async onTimeSpentBlur () {
		if (this.userRoleTimeTrackingTimeEntriesFeatureEnabled) {
			await this.timeEntries.applyTimeSpentChange(this.user.timeSpent ?? 0)
		} else {
			this.$emit("input", { ...this.user })
		}
	}

	/**
	 * Updates the local user's location and emits an `input` event.
	 * @param location The newly selected location.
	 */
	private updateLocation (location: Location | null | undefined) {
		this.user.location = location
		this.$emit("input", { ...this.user })
	}

	/**
	 * Updates local users time entries and timeSpent, then emits `input`.
	 * @param updatedEntries The updated list of time entries.
	 */
	private updateTimeEntries (updatedEntries: TimeEntryType[]) {
		this.user.timeEntries = updatedEntries
		this.user.timeSpent = this.calculateTotalTimeSpent(updatedEntries)
		this.$emit("input", { ...this.user })
	}

	/**
	 * Emits a `remove` event with the user's ID to notify parent components.
	 */
	private remove () {
		this.$emit("remove", this.user.id)
	}
}
